import { Navigate, useParams } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { PreAuthsSettings } from '@/features/preauths/PreAuthsSettings';
import { FollowUpSettings } from '@/features/insurance-ar/FollowUpSettings';
import { VirtualCardsSettings } from '@/features/document-center/VirtualCardsSettings';
import { AppealsSettings } from '@/features/appeals/AppealsSettings';
import { PmsAccessBody } from '@/features/pms-access';
import { NotFoundPage } from '@/features/shared/pages/NotFoundPage';
import { SettingsShell } from '@/features/settings-d/SettingsShell';
import {
  RCM_SETTINGS_DEFAULT_SLUG,
  RCM_SETTINGS_LEGACY_SLUGS,
  itemFor,
  type RcmSettingsSlug,
} from '@/features/settings-d/settingsData';

/**
 * RCM Settings — direction D (new IA decided Oct 15). The open page lives in
 * the URL slug under `/settings/`. Legacy staging slugs redirect to their new
 * home. Built pages (Pre-auths, Follow-up, Appeals, Virtual cards) are mounted
 * as slots inside the new shell until their own direction-D pass.
 */
export function SettingsPage() {
  usePageTopBar({ hideTopBar: true });
  const { page, payerId } = useParams<{ page?: string; payerId?: string }>();
  // /settings/payers/:payerId is one payer's page inside Payer rules.
  const slug = payerId !== undefined ? 'payers' : page;

  // Legacy redirect (e.g. /settings/ai-dispatch-policies → /settings/autopilot).
  if (slug && RCM_SETTINGS_LEGACY_SLUGS[slug]) {
    return <Navigate to={`/settings/${RCM_SETTINGS_LEGACY_SLUGS[slug]}`} replace />;
  }

  // A slug that is not a settings page is a route that doesn't exist: the app's 404,
  // never another settings page in its place.
  if (slug && !itemFor(slug)) return <NotFoundPage />;

  const active: RcmSettingsSlug =
    slug && itemFor(slug) ? (slug as RcmSettingsSlug) : RCM_SETTINGS_DEFAULT_SLUG;

  return (
    <SettingsShell
      slug={active}
      {...(payerId !== undefined ? { payerId } : {})}
      slots={{
        'pre-auths': <PreAuthsSettings />,
        'follow-up': <FollowUpSettings />,
        appeals: <AppealsSettings />,
        'virtual-cards': <VirtualCardsSettings />,
        // Reads the RCM backend: connection state, lag, last sync,
        // the blockers and the stop switch (P13-09).
        pms: <PmsAccessBody />,
      }}
    />
  );
}
